import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Trash2, X } from 'lucide-react';
import { getArtworkDependencies, deleteArtworkPermanent } from '../lib/artworksApi';

/**
 * Confirmation modal for permanently deleting an artwork.
 * Fetches dependency counts on open so the admin knows exactly what they'll lose.
 * Fires `onDeleted` after a successful DELETE so the parent can refresh + navigate.
 */
export default function DeleteArtworkModal({ artworkId, artworkTitle, open, onClose, onDeleted }) {
  const [deps, setDeps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !artworkId) return;
    setDeps(null);
    setConfirmText('');
    setLoading(true);
    getArtworkDependencies(artworkId)
      .then((d) => setDeps(d))
      .catch(() => setDeps({ counts: {} }))
      .finally(() => setLoading(false));
  }, [open, artworkId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const doDelete = async () => {
    setDeleting(true);
    const t = toast.loading('Deleting artwork…');
    try {
      const res = await deleteArtworkPermanent(artworkId);
      toast.success(`Artwork deleted permanently${res.orphaned_offer_documents_removed ? ` (+${res.orphaned_offer_documents_removed} offer doc${res.orphaned_offer_documents_removed === 1 ? '' : 's'} removed)` : ''}`, { id: t });
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed', { id: t });
    } finally {
      setDeleting(false);
    }
  };

  const c = deps?.counts || {};
  const requiredWord = 'DELETE';
  const canDelete = confirmText.trim().toUpperCase() === requiredWord && !deleting;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-[#050505]/85 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="delete-artwork-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0a0a0a] border border-[#e28f8f]/40 p-6 md:p-8"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 border border-transparent hover:border-[#333] text-[#666] hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center border border-[#e28f8f]/40 bg-[#e28f8f]/8 text-[#e28f8f] shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#e28f8f]">
                  Permanent delete
                </div>
                <h2 className="mt-1 text-lg font-outfit font-semibold text-white leading-tight">
                  {artworkTitle || 'this artwork'}
                </h2>
              </div>
            </div>

            <p className="text-[12px] font-mono text-[#c8c8c8] leading-relaxed mb-4">
              This will remove the artwork from Supabase permanently. This action cannot be undone.
              If you&apos;re not sure, use <span className="text-[#e4c07a]">Archive</span> instead — archived
              artworks are hidden but can be restored later.
            </p>

            {loading && (
              <div className="flex items-center gap-2 text-[11px] font-mono text-[#666] py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking dependencies…
              </div>
            )}
            {!loading && deps && (
              <div className="bg-[#050505] border border-[#1a1a1a] p-3 mb-5" data-testid="delete-artwork-deps">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-2">
                  Will also cascade
                </div>
                <ul className="space-y-1 text-[12px] font-mono">
                  <DepRow label="Additional images" value={c.images} tone={c.images > 0 ? 'strong' : 'muted'} />
                  <DepRow label="Inquiries" value={c.inquiries} tone={c.inquiries > 0 ? 'warn' : 'muted'} />
                  <DepRow label="PDF offer entries" value={c.offer_items} tone={c.offer_items > 0 ? 'warn' : 'muted'} />
                  {c.orphaned_offer_documents > 0 && (
                    <DepRow label="Offer documents removed" value={c.orphaned_offer_documents} tone="warn" />
                  )}
                </ul>
              </div>
            )}

            <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-1.5">
              Type <span className="text-[#e28f8f]">DELETE</span> to confirm
            </label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-artwork-confirm-input"
              autoComplete="off"
              placeholder="DELETE"
              className="w-full bg-[#050505] border border-[#1a1a1a] px-3 py-2.5 text-[13px] font-mono text-[#f6f6f6] tracking-[0.14em] uppercase focus:outline-none focus:border-[#e28f8f]/50 mb-4"
            />

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={deleting}
                className="text-[11px] font-mono uppercase tracking-[0.16em] px-4 py-2.5 border border-[#1a1a1a] text-[#888] hover:text-white hover:border-[#333] transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={!canDelete}
                data-testid="delete-artwork-confirm-btn"
                className="text-[11px] font-mono uppercase tracking-[0.16em] px-4 py-2.5 border transition-colors inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  color: canDelete ? '#050505' : '#e28f8f',
                  background: canDelete ? '#e28f8f' : 'transparent',
                  borderColor: '#e28f8f',
                }}
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DepRow({ label, value, tone }) {
  const val = value ?? 0;
  const color = tone === 'warn' ? '#e4c07a' : tone === 'strong' ? '#f6f6f6' : '#666';
  return (
    <li className="flex items-baseline justify-between">
      <span className="text-[#8a8a8a]">{label}</span>
      <span style={{ color }}>{val}</span>
    </li>
  );
}
